import { useState, type ReactNode } from "react";

import { Info, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCan } from "@/lib/auth/useCan";
import { cn } from "@/lib/utils";

import { type ContractTypeRow, type StoredPlaybook } from "../api";
import { EditPlaybookForm } from "./EditPlaybookForm";
import {
  type BadgeTone,
  type Callout as CalloutT,
  type ContractTypePlaybook,
  type LifecycleStage,
  type MustHaveField,
  type ThreeOutputs,
  resolvePlaybook,
} from "./content";

/**
 * Quick view (narrow drawer) = the basic contract-type record.
 * Expanded view (full-page) = the full lifecycle playbook.
 * See content.ts for the data-readiness design (static ⊕ override).
 */

// ── shared bits ──────────────────────────────────────────────────────────────
// Map the doc's badge colors onto Ops semantic tokens (theme-aware, no dark: needed).
const TONE_BADGE: Record<BadgeTone, string> = {
  blue: "bg-info-light text-info",
  green: "bg-success-light text-success",
  purple: "bg-power-light text-power",
  coral: "bg-danger-light text-danger",
  yellow: "bg-warning-light text-warning",
};

const str = (v: unknown): string => (v == null || v === "" ? "—" : String(v));

/** Render UPPER_SNAKE_CASE field codes (ACCOUNT_ID, MRR_TOTAL…) as inline code. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(\b[A-Z][A-Z0-9_]{2,}\b)/g);
  return (
    <>
      {parts.map((p, i) =>
        /^[A-Z][A-Z0-9_]{2,}$/.test(p) ? (
          <code
            key={i}
            className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-brand"
          >
            {p}
          </code>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

function PlaybookSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm leading-relaxed">{children}</CardContent>
    </Card>
  );
}

function Lifecycle({ stages }: { stages: LifecycleStage[] }) {
  return (
    <div className="flex flex-wrap items-stretch gap-2">
      {stages.map((s, i) => (
        <div key={i} className="flex items-stretch gap-2">
          <div className="min-w-[120px] flex-1 rounded-lg border bg-muted/40 px-3 py-2">
            <div className="text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
              {s.stage}
            </div>
            <div className="text-[13px] font-semibold">{s.title}</div>
            {s.detail && <div className="mt-0.5 text-[11px] text-muted-foreground">{s.detail}</div>}
          </div>
          {i < stages.length - 1 && (
            <span className="flex items-center font-bold text-brand" aria-hidden>
              →
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function Chips({ fields }: { fields: MustHaveField[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {fields.map((f) => (
        <span
          key={f.label}
          className={cn(
            "rounded-lg border px-2.5 py-1 text-xs font-medium",
            f.required ? "border-danger/50" : "border-border bg-muted/40",
          )}
        >
          <RichText text={f.label} />
        </span>
      ))}
    </div>
  );
}

const CALLOUT_TONE: Record<CalloutT["tone"], string> = {
  rule: "border-l-4 border-brand bg-muted/50",
  warn: "border border-dashed border-danger/50 bg-danger-light/30",
  note: "border border-dashed border-power/50 bg-power-light/30",
};

function Callout({ callout }: { callout: CalloutT }) {
  return (
    <div className={cn("rounded-md px-3.5 py-2.5 text-[13px]", CALLOUT_TONE[callout.tone])}>
      {callout.heading && <span className="font-semibold">{callout.heading}: </span>}
      <RichText text={callout.body} />
    </div>
  );
}

const OUTPUT_LABELS: [keyof ThreeOutputs, string][] = [
  ["billingStart", "Billing start"],
  ["churn", "Churn"],
  ["bookedVsBilled", "Booked-vs-billed"],
  ["renewals", "Renewals"],
];

function Outputs({ outputs }: { outputs: ThreeOutputs }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {OUTPUT_LABELS.filter(([k]) => outputs[k]).map(([k, label]) => (
        <div key={k} className="flex flex-col gap-0.5">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </dt>
          <dd className="text-[13px]">
            <RichText text={outputs[k]!} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function WaitingRealData() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-dashed border-warning/70 bg-warning-light/40 px-3.5 py-2.5 text-[13px]">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
      <div>
        <span className="font-semibold">Waiting real data…</span> This lifecycle is the standard
        playbook for this contract type, shown as a reference. It isn't yet sourced from live data —
        an admin can refine it per type.
      </div>
    </div>
  );
}

// ── Quick view (narrow): the basic record ────────────────────────────────────
export function ContractTypeQuickView({ row }: { row: ContractTypeRow }) {
  const resolved = resolvePlaybook(row.name);
  return (
    <div className="space-y-4 p-4">
      <PlaybookSection title="Details">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Name">{str(row.name)}</Field>
          <Field label="Contracts Using">{str(row.contract_count)}</Field>
          <div className="col-span-2">
            <Field label="Description">{str(row.descr)}</Field>
          </div>
        </dl>
      </PlaybookSection>
      {resolved && (
        <p className="px-1 text-[13px] text-muted-foreground">
          {resolved.playbook.summary} Open the name to view the full lifecycle playbook.
        </p>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children?: ReactNode }) {
  const empty = children == null || children === "" || children === "—";
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{empty ? "—" : children}</dd>
    </div>
  );
}

// ── Expanded view (full-page): the playbook ──────────────────────────────────
export function ContractTypePlaybookView({
  row,
  stored,
}: {
  row: ContractTypeRow;
  /** Ops-persisted override (admin-edited). Absent → emulated archetype default. */
  stored?: StoredPlaybook;
}) {
  const canEdit = useCan("contracts.admin");
  const [editing, setEditing] = useState(false);
  const resolved = resolvePlaybook(
    row.name,
    stored?.data as Partial<ContractTypePlaybook> | undefined,
  );
  const base: ContractTypePlaybook = resolved?.playbook ?? {
    key: "custom",
    label: row.name ?? "Contract type",
    summary: "",
  };

  if (editing) {
    return <EditPlaybookForm row={row} current={base} onDone={() => setEditing(false)} />;
  }

  // Type the doc doesn't cover (e.g. Referral) and no edited data yet.
  if (!resolved) {
    return (
      <div className="space-y-4 p-4">
        <Header
          row={row}
          onEdit={canEdit ? () => setEditing(true) : undefined}
          editLabel="Add playbook"
        />
        <div className="rounded-md border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
          No lifecycle playbook is defined for this contract type yet.
        </div>
      </div>
    );
  }

  const p = resolved.playbook;
  return (
    <div className="space-y-4 p-4">
      <Header
        row={row}
        badge={p.badge}
        summary={p.summary}
        onEdit={canEdit ? () => setEditing(true) : undefined}
      />
      {resolved.emulated ? <WaitingRealData /> : <LastEdited stored={stored} />}

      {p.lifecycle && (
        <PlaybookSection title="Lifecycle">
          <Lifecycle stages={p.lifecycle} />
        </PlaybookSection>
      )}
      {p.whenToUse && (
        <PlaybookSection title="When to use it">
          <RichText text={p.whenToUse} />
        </PlaybookSection>
      )}
      {p.mustHave && p.mustHave.length > 0 && (
        <PlaybookSection title="Must-have at creation">
          <Chips fields={p.mustHave} />
        </PlaybookSection>
      )}
      {p.billingStart && (
        <PlaybookSection title="How billing-start is determined">
          <RichText text={p.billingStart} />
        </PlaybookSection>
      )}
      {p.termRenewal && (
        <PlaybookSection title="Term & renewal">
          <RichText text={p.termRenewal} />
        </PlaybookSection>
      )}
      {p.howItEnds && p.howItEnds.length > 0 && (
        <PlaybookSection title="How it ends">
          <ul className="ml-4 list-disc space-y-1.5">
            {p.howItEnds.map((item, i) => (
              <li key={i}>
                <RichText text={item} />
              </li>
            ))}
          </ul>
        </PlaybookSection>
      )}
      {p.keyPoints && p.keyPoints.items.length > 0 && (
        <PlaybookSection title={p.keyPoints.heading}>
          <ul className="ml-4 list-disc space-y-1.5">
            {p.keyPoints.items.map((item, i) => (
              <li key={i}>
                <RichText text={item} />
              </li>
            ))}
          </ul>
        </PlaybookSection>
      )}
      {p.outputs && (
        <PlaybookSection title="In the three outputs">
          <Outputs outputs={p.outputs} />
        </PlaybookSection>
      )}
      {p.callouts && p.callouts.length > 0 && (
        <div className="space-y-2">
          {p.callouts.map((c, i) => (
            <Callout key={i} callout={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function Header({
  row,
  badge,
  summary,
  onEdit,
  editLabel = "Edit",
}: {
  row: ContractTypeRow;
  badge?: { text: string; tone: BadgeTone };
  summary?: string;
  onEdit?: () => void;
  editLabel?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-bold">{str(row.name)}</h3>
        {badge && (
          <span
            className={cn(
              "rounded px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide",
              TONE_BADGE[badge.tone],
            )}
          >
            {badge.text}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {row.contract_count ?? 0} {row.contract_count === 1 ? "contract" : "contracts"}
          </span>
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
              {editLabel}
            </Button>
          )}
        </div>
      </div>
      {summary && <p className="text-[13px] text-muted-foreground">{summary}</p>}
    </div>
  );
}

function LastEdited({ stored }: { stored?: StoredPlaybook }) {
  if (!stored) return null;
  const when = stored.updatedAt ? new Date(stored.updatedAt).toLocaleDateString() : null;
  return (
    <div className="rounded-md border border-success/40 bg-success-light/30 px-3.5 py-2 text-[13px] text-muted-foreground">
      <span className="font-semibold text-foreground">Saved playbook</span>
      {stored.updatedByName ? ` · last edited by ${stored.updatedByName}` : ""}
      {when ? ` · ${when}` : ""}
    </div>
  );
}
